/*
 *    Copyright [2007-2025] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       https://github.com/wisemapping/wisemapping-open-source/blob/main/LICENSE.md
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
import { EditorRenderMode } from '@wisemapping/mindplot';
import ActionType from '../action-type';

class Capability {
  readonly mode: EditorRenderMode;
  readonly isLocked: boolean;
  readonly isMobile: boolean;

  constructor(mode: EditorRenderMode, isLocked: boolean) {
    this.mode = mode;
    this.isMobile = this.parseMobile();
    this.isLocked = isLocked;
  }

  private parseMobile = () => {
    const result =
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
        navigator.userAgent.toLowerCase(),
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
        navigator.userAgent.toLowerCase().substring(0, 4),
      );
    return result;
  };

  isHidden(action: ActionType): boolean {
    const mapping = ActionConfigByRenderMode[action];

    let result = false;
    if (mapping) {
      // Has been marked in desktop ...
      const desktopCapability: EditorRenderMode[] | undefined = mapping.desktop?.hidden;
      result = Boolean(desktopCapability?.includes(this.mode)).valueOf();

      // Had been overwrited for mobile ...
      if (!result && this.isMobile) {
        const mobileCapability: EditorRenderMode[] | undefined = mapping.mobile?.hidden;
        result = Boolean(mobileCapability?.includes(this.mode)).valueOf();
      }
    }

    // Todo: Needs to be moved to more declarative ...
    if (
      this.isLocked &&
      (action === 'save' ||
        action === 'keyboard-shortcuts' ||
        action === 'edition-toolbar' ||
        action === 'publish' ||
        action === 'redo-changes' ||
        action === 'undo-changes' ||
        action === 'paste-as-child')
    ) {
      result = true;
    }
    return Boolean(result);
  }

  isDisabled(action: ActionType): boolean {
    let result: boolean = this.isHidden(action);

    // If it was not marked as hidden, it might be marked as disabled ...
    if (!result) {
      const mapping = ActionConfigByRenderMode[action];

      if (mapping) {
        // Has been marked in desktop ...
        const desktopCapability: EditorRenderMode[] | undefined = mapping.desktop?.hidden;
        result = Boolean(desktopCapability?.includes(this.mode)).valueOf();

        // Had been overwrited for mobile ...
        if (!result && this.isMobile) {
          const mobileCapability: EditorRenderMode[] | undefined = mapping.mobile?.hidden;
          result = Boolean(mobileCapability?.includes(this.mode)).valueOf();
        }
      }
    }

    return Boolean(result);
  }
}

interface CapabilitySupport {
  mobile?: {
    hidden?: EditorRenderMode[];
    disabled?: EditorRenderMode[];
  };
  desktop?: {
    hidden?: EditorRenderMode[];
    disabled?: EditorRenderMode[];
  };
}

const ActionConfigByRenderMode: Record<ActionType, CapabilitySupport> = {
  'redo-changes': {
    desktop: {
      hidden: ['viewonly-public', 'viewonly-private', 'edition-viewer'],
    },
  },
  'undo-changes': {
    desktop: {
      hidden: ['viewonly-public', 'viewonly-private', 'edition-viewer'],
    },
  },
  save: {
    desktop: {
      hidden: ['showcase', 'viewonly-public', 'viewonly-private', 'edition-viewer'],
    },
  },
  print: {
    desktop: {
      hidden: ['showcase', 'desktop'],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
        'desktop',
      ],
    },
  },
  publish: {
    desktop: {
      hidden: [
        'showcase',
        'viewonly-public',
        'viewonly-private',
        'edition-viewer',
        'edition-editor',
        'desktop',
      ],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
        'desktop',
      ],
    },
  },
  share: {
    desktop: {
      hidden: [
        'showcase',
        'viewonly-public',
        'viewonly-private',
        'edition-viewer',
        'edition-editor',
        'desktop',
      ],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
        'desktop',
      ],
    },
  },
  info: {
    desktop: {
      hidden: ['showcase', 'desktop'],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
        'desktop',
      ],
    },
  },
  account: {
    desktop: {
      hidden: ['showcase', 'desktop'],
    },
  },
  'edition-toolbar': {
    desktop: {
      hidden: ['viewonly-public', 'viewonly-private', 'edition-viewer'],
    },
  },
  'keyboard-shortcuts': {
    desktop: {
      hidden: ['viewonly-public', 'viewonly-private', 'edition-viewer'],
    },
    mobile: {
      hidden: ['edition-editor', 'edition-owner', 'showcase'],
    },
  },
  history: {
    desktop: {
      hidden: ['viewonly-public', 'viewonly-private', 'edition-viewer', 'showcase', 'desktop'],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
        'desktop',
      ],
    },
  },
  export: {
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
      ],
    },
  },
  'appbar-title': {
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
      ],
    },
  },
  'sign-up': {
    desktop: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
        'desktop',
      ],
    },
  },
  starred: {
    desktop: {
      hidden: ['showcase', 'viewonly-private', 'viewonly-public', 'desktop'],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
        'desktop',
      ],
    },
  },
  theme: {
    desktop: {
      hidden: ['viewonly-public', 'viewonly-private', 'edition-viewer'],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
      ],
    },
  },
  rename: {
    desktop: {
      hidden: [
        'showcase',
        'viewonly-public',
        'viewonly-private',
        'edition-viewer',
        'edition-editor',
      ],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
      ],
    },
  },
  'outline-view': {
    desktop: {
      hidden: ['showcase'],
    },
    mobile: {
      hidden: [
        'viewonly-public',
        'viewonly-private',
        'showcase',
        'edition-viewer',
        'edition-editor',
        'edition-owner',
      ],
    },
  },
  layout: {
    desktop: {
      hidden: ['showcase', 'viewonly-public', 'viewonly-private', 'edition-viewer', 'desktop'],
    },
  },
  'paste-as-child': {
    desktop: {
      hidden: ['viewonly-public', 'viewonly-private', 'edition-viewer'],
    },
  },
  'copy-link-to-node': {
    desktop: {
      hidden: ['viewonly-public', 'viewonly-private', 'edition-viewer'],
    },
  },
};
export default Capability;
